import { Client, Room } from 'colyseus.js';
import { SERVER_PORT, ROOM_NAME } from '@wooorms/shared';
import type { JoinOptions } from '@wooorms/shared';

/**
 * Connection states for the network manager
 */
export enum ConnectionState {
  DISCONNECTED = 'disconnected',
  CONNECTING = 'connecting',
  CONNECTED = 'connected',
  RECONNECTING = 'reconnecting',
  ERROR = 'error',
}

/**
 * Event types emitted by NetworkManager
 */
export interface NetworkEvents {
  stateChange: (state: ConnectionState) => void;
  connected: (room: Room) => void;
  disconnected: (code?: number, reason?: string) => void;
  error: (error: Error) => void;
  message: (type: string, data: unknown) => void;
}

/**
 * Configuration for NetworkManager
 */
export interface NetworkConfig {
  /** Server URL (defaults to localhost:2567) */
  serverUrl: string;
  /** Maximum reconnection attempts */
  maxReconnectAttempts: number;
  /** Delay between reconnection attempts (ms) */
  reconnectDelay: number;
  /** Reconnection delay multiplier */
  reconnectDelayMultiplier: number;
  /** Maximum reconnection delay (ms) */
  maxReconnectDelay: number;
}

/**
 * Get the server URL from environment or use default
 */
function getServerUrl(): string {
  // Vite injects environment variables at build time
  // @ts-expect-error - Vite env variable
  const envUrl = import.meta.env?.VITE_SERVER_URL;
  if (envUrl) {
    return envUrl;
  }

  // Default to localhost for development
  const protocol = typeof window !== 'undefined' && window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//localhost:${SERVER_PORT}`;
}

/**
 * Default network configuration
 */
const DEFAULT_CONFIG: NetworkConfig = {
  serverUrl: getServerUrl(),
  maxReconnectAttempts: 5,
  reconnectDelay: 1000,
  reconnectDelayMultiplier: 1.5,
  maxReconnectDelay: 10000,
};

/**
 * NetworkManager - Handles Colyseus server connection and reconnection
 */
export class NetworkManager {
  /** Colyseus client instance */
  private client: Client;

  /** Current room connection */
  private room: Room | null = null;

  /** Current connection state */
  private state: ConnectionState = ConnectionState.DISCONNECTED;

  /** Configuration */
  private config: NetworkConfig;

  /** Event listeners */
  private listeners: Map<string, Set<Function>> = new Map();

  /** Reconnection state */
  private reconnectAttempts: number = 0;
  private reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  private lastJoinOptions: JoinOptions | null = null;

  /** Reconnection token for Colyseus reconnection */
  private lastReconnectionToken: string | null = null;

  constructor(config: Partial<NetworkConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.client = new Client(this.config.serverUrl);
  }

  /**
   * Get the current connection state
   */
  getState(): ConnectionState {
    return this.state;
  }

  /**
   * Get the current room (if connected)
   */
  getRoom(): Room | null {
    return this.room;
  }

  /**
   * Get the local player's session ID
   */
  getSessionId(): string | null {
    return this.room?.sessionId ?? null;
  }

  /**
   * Check if connected to a room
   */
  isConnected(): boolean {
    return this.state === ConnectionState.CONNECTED && this.room !== null;
  }

  /**
   * Connect to the game server
   */
  async connect(options: JoinOptions): Promise<Room> {
    if (this.state === ConnectionState.CONNECTING) {
      throw new Error('Already connecting');
    }

    if (this.state === ConnectionState.CONNECTED) {
      await this.disconnect();
    }

    this.lastJoinOptions = options;
    this.setState(ConnectionState.CONNECTING);

    try {
      // Try to join or create a room
      this.room = await this.client.joinOrCreate(ROOM_NAME, options);

      // Store reconnection token for reconnection attempts
      this.lastReconnectionToken = this.room.reconnectionToken;

      // Setup room event handlers
      this.setupRoomHandlers();

      // Reset reconnection state
      this.reconnectAttempts = 0;

      this.setState(ConnectionState.CONNECTED);
      this.emit('connected', this.room);

      return this.room;
    } catch (error) {
      this.setState(ConnectionState.ERROR);
      const err = error instanceof Error ? error : new Error(String(error));
      this.emit('error', err);
      throw err;
    }
  }

  /**
   * Disconnect from the current room
   */
  async disconnect(): Promise<void> {
    this.cancelReconnect();

    if (this.room) {
      try {
        await this.room.leave(true);
      } catch (error) {
        console.warn('Error leaving room:', error);
      }
      this.room = null;
    }

    this.setState(ConnectionState.DISCONNECTED);
    this.emit('disconnected');
  }

  /**
   * Send a message to the server
   */
  send(type: string | number, data?: unknown): void {
    if (!this.room) {
      console.warn('Cannot send message: not connected');
      return;
    }

    this.room.send(type, data);
  }

  /**
   * Add an event listener
   */
  on<K extends keyof NetworkEvents>(event: K, callback: NetworkEvents[K]): void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(callback);
  }

  /**
   * Remove an event listener
   */
  off<K extends keyof NetworkEvents>(event: K, callback: NetworkEvents[K]): void {
    const callbacks = this.listeners.get(event);
    if (callbacks) {
      callbacks.delete(callback);
    }
  }

  /**
   * Add a one-time event listener
   */
  once<K extends keyof NetworkEvents>(event: K, callback: NetworkEvents[K]): void {
    const wrapper = ((...args: unknown[]) => {
      this.off(event, wrapper as NetworkEvents[K]);
      (callback as Function)(...args);
    }) as NetworkEvents[K];
    this.on(event, wrapper);
  }

  /**
   * Setup handlers for room events
   */
  private setupRoomHandlers(): void {
    if (!this.room) return;

    // Handle room leave
    this.room.onLeave((code: number) => {
      // Code 1000 = normal close, 4000+ = server closed
      const wasIntentional = code === 1000;

      if (!wasIntentional && this.reconnectAttempts < this.config.maxReconnectAttempts) {
        this.attemptReconnect();
      } else {
        this.room = null;
        this.setState(ConnectionState.DISCONNECTED);
        this.emit('disconnected', code);
      }
    });

    // Handle room errors
    this.room.onError((code: number, message?: string) => {
      console.error(`Room error (${code}): ${message}`);
      this.emit('error', new Error(`Room error (${code}): ${message}`));
    });

    // Forward room messages
    this.room.onMessage('*', (type: unknown, data: unknown) => {
      this.emit('message', String(type), data);
    });
  }

  /**
   * Attempt to reconnect to the room
   */
  private async attemptReconnect(): Promise<void> {
    if (this.state === ConnectionState.RECONNECTING) {
      return;
    }

    this.setState(ConnectionState.RECONNECTING);
    this.reconnectAttempts++;

    const delay = Math.min(
      this.config.reconnectDelay * Math.pow(this.config.reconnectDelayMultiplier, this.reconnectAttempts - 1),
      this.config.maxReconnectDelay
    );

    this.reconnectTimeout = setTimeout(async () => {
      try {
        // Try to reconnect with the reconnection token
        if (this.lastReconnectionToken) {
          try {
            this.room = await this.client.reconnect(this.lastReconnectionToken);
            this.lastReconnectionToken = this.room.reconnectionToken;
            this.setupRoomHandlers();
            this.reconnectAttempts = 0;
            this.setState(ConnectionState.CONNECTED);
            this.emit('connected', this.room);
            return;
          } catch {
            // Reconnection failed, try fresh connection
          }
        }

        // If reconnection fails, try a fresh connection
        if (this.lastJoinOptions) {
          await this.connect(this.lastJoinOptions);
        } else {
          throw new Error('No join options available for reconnection');
        }
      } catch (error) {
        console.error('Reconnection attempt failed:', error);

        if (this.reconnectAttempts < this.config.maxReconnectAttempts) {
          this.attemptReconnect();
        } else {
          this.setState(ConnectionState.ERROR);
          this.emit('error', new Error('Max reconnection attempts reached'));
          this.emit('disconnected', -1, 'Max reconnection attempts reached');
        }
      }
    }, delay);
  }

  /**
   * Cancel any pending reconnection attempt
   */
  private cancelReconnect(): void {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
    this.reconnectAttempts = 0;
  }

  /**
   * Set the connection state and emit change event
   */
  private setState(newState: ConnectionState): void {
    if (this.state !== newState) {
      this.state = newState;
      this.emit('stateChange', newState);
    }
  }

  /**
   * Emit an event to all listeners
   */
  private emit(event: string, ...args: unknown[]): void {
    const callbacks = this.listeners.get(event);
    if (callbacks) {
      callbacks.forEach((callback) => {
        try {
          callback(...args);
        } catch (error) {
          console.error(`Error in ${event} handler:`, error);
        }
      });
    }
  }

  /**
   * Clean up resources
   */
  destroy(): void {
    this.cancelReconnect();

    if (this.room) {
      this.room.leave(false);
      this.room = null;
    }

    this.listeners.clear();
    this.state = ConnectionState.DISCONNECTED;
  }
}
